/**
 * 本文の中で変わった語を強調して描く。
 *
 * セルの地色は「この項目が変わった」までしか言わない。**変わったのが 1 語でも、読み手が読む
 * 量はセル全体である。** 変わった区間だけを濃く塗れば、読む量は変わった語だけで済む。
 *
 * 本文はここだけで描かれるわけではない。用語リンク（`TermText`）と変数参照（`VariableText`）が
 * 先に本文を切っており、強調はその切れ端の上にも掛かる必要がある。**切れ端が全体本文の何文字目
 * から始まるかを渡す形**にして、切り方を知らないまま同じ区間を当てられるようにしてある
 * （`splitByRanges`）。切る前に区間を相対座標へ直す形にすると、切り方を変えるたびに座標の
 * 直し方も書き換えることになり、片方だけ直った状態が生まれる。
 *
 * 強調に `<mark>` を使うのは、これが「文書の中で注意を向けたい箇所」を表す要素だからである。
 * `<span>` に地色を塗るだけだと、色を読めない読み手には強調そのものが届かない。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 本文の中で変わった区間だけを強調して描く。
 * @graph-connects diffWords [calls] 変更前と変更後の突き合わせ
 * @graph-connects splitByRanges [calls] 変更区間での本文の割り方
 * @graph-connects SPEC_DIFF_WORD_CLASS [references] 側ごとの語の地色
 */
import { Fragment } from 'react';

import { diffWords, splitByRanges, type DiffRange } from '../../lib/word-diff.js';
import type { DiffSide } from './DiffSplitPanes.js';
import { SPEC_DIFF_WORD_CLASS } from './SpecDiffState.js';

/** 本文の中で変わった語の強調指定。 */
export interface WordHighlight {
  /** 全体本文の座標での変更区間。 */
  ranges: readonly DiffRange[];
  /** 本文がどちら側の姿か。左は消えた語を赤、右は入った語を緑で塗る。 */
  side: DiffSide;
}

/** `HighlightedText` の props。 */
export interface HighlightedTextProps {
  /** 描く本文。全体本文の一部でよい。 */
  text: string;
  /** `text` の先頭が全体本文の何文字目か。切られていなければ 0。 */
  offset?: number;
  /** 強調指定。PJ を選んでいない・変わっていないなら `null`。 */
  highlight?: WordHighlight | null;
}

/**
 * 本文を描き、変わった区間だけを強調する。
 *
 * 強調指定が無いときは本文をそのまま返す。**空の `<span>` で包まない** — 包むと、強調が要らない
 * 場合にだけ余計な要素が増え、行間や折り返しが強調のある行と揃わなくなる。
 *
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 本文を描き、変わった区間だけをその側の色で強調する。
 * @graph-connects splitByRanges [calls] 変更区間での本文の割り方
 * @graph-connects SPEC_DIFF_WORD_CLASS [references] 側ごとの語の地色
 */
export function HighlightedText({ text, offset = 0, highlight = null }: HighlightedTextProps) {
  if (highlight === null || highlight.ranges.length === 0) return <>{text}</>;

  const parts = splitByRanges(text, offset, highlight.ranges);

  return (
    <>
      {parts.map((part, index) =>
        part.changed ? (
          <mark
            key={String(index)}
            className={`${SPEC_DIFF_WORD_CLASS[highlight.side]} text-inherit`}
          >
            {part.text}
          </mark>
        ) : (
          <Fragment key={String(index)}>{part.text}</Fragment>
        ),
      )}
    </>
  );
}

/**
 * 変更前と変更後の本文から、指定した側の強調指定を組み立てる。
 *
 * **変更前が無ければ `null` を返す。** 比べる相手が無いのに区間を作ると、本文全体が「入った語」
 * として塗られ、セルの地色と同じ情報しか出さない濃い帯になる。
 *
 * @param before 変更前の本文。PJ を選んでいない・その行が書き換えられていないなら `null`
 * @param after 変更後の本文
 * @param side どちら側の姿を描くか
 * @returns 強調指定。比べる相手が無ければ `null`
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 変更前と変更後の本文から、指定した側で強調する区間を組み立てる。
 * @graph-connects diffWords [calls] 変更前と変更後の突き合わせ
 */
export function toWordHighlight(
  before: string | null,
  after: string,
  side: DiffSide = 'after',
): WordHighlight | null {
  if (before === null) return null;

  const diff = diffWords(before, after);
  const ranges = side === 'before' ? diff.before : diff.after;

  return ranges.length === 0 ? null : { ranges, side };
}
